import { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { respingiConferimento } from '../../api/conferimenti';
import type { ConferimentoDetail, RespingiCausale } from '../../api/conferimenti';

const CAUSALI: { value: RespingiCausale; labelKey: string }[] = [
  { value: 'radiometrico', labelKey: 'conferimenti.respingi_modal.causale_radiometrico' },
  { value: 'documenti_mancanti', labelKey: 'conferimenti.respingi_modal.causale_documenti' },
  { value: 'non_conforme_omologa', labelKey: 'conferimenti.respingi_modal.causale_non_conforme' },
  { value: 'peso_eccedente', labelKey: 'conferimenti.respingi_modal.causale_peso' },
  { value: 'altro', labelKey: 'conferimenti.respingi_modal.causale_altro' },
];

const schema = z.object({
  causale: z.enum(['peso_eccedente', 'non_conforme_omologa', 'radiometrico', 'documenti_mancanti', 'altro']),
  motivo: z.string().optional(),
});
type FormData = z.infer<typeof schema>;

const inputCls =
  'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

interface Props {
  conferimentoId: string;
  numero: string | null;
  onClose: () => void;
  onSaved: (updated: ConferimentoDetail) => void;
}

export default function RespingiConferimentoModal({ conferimentoId, numero, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [serverError, setServerError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  async function onSubmit(data: FormData) {
    setServerError(null);
    try {
      const updated = await respingiConferimento(conferimentoId, {
        causale: data.causale,
        motivo: data.motivo?.trim() || undefined,
      });
      onSaved(updated);
    } catch {
      setServerError(t('conferimenti.respingi_modal.errore_salvataggio'));
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid">
          <h2 className="text-base font-semibold text-ink-dark flex items-center gap-2">
            <AlertTriangle size={16} className="text-accent-danger" />
            {t('conferimenti.respingi_modal.titolo')}
          </h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="px-6 py-5 space-y-4">
            <p className="text-sm text-ink-mid">
              <Trans i18nKey="conferimenti.respingi_modal.descrizione" values={{ numero: numero ?? '—' }} components={{ strong: <strong /> }} />
            </p>
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.respingi_modal.causale')}</label>
              <select className={`${inputCls} cursor-pointer`} {...register('causale')}>
                <option value="">{t('conferimenti.respingi_modal.causale_seleziona')}</option>
                {CAUSALI.map((c) => <option key={c.value} value={c.value}>{t(c.labelKey)}</option>)}
              </select>
              {errors.causale && <p className="text-xs text-accent-danger mt-1">{t('conferimenti.respingi_modal.causale_obbligatoria')}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.respingi_modal.note')}</label>
              <textarea rows={2} className={inputCls} placeholder={t('conferimenti.respingi_modal.note_placeholder')} {...register('motivo')} />
            </div>
            {serverError && (
              <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">{serverError}</div>
            )}
          </div>
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg">
            <button type="button" onClick={onClose} disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">{t('common.azioni.annulla')}</button>
            <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-accent-danger rounded-lg hover:bg-[#B91C1C] transition-colors disabled:opacity-60">
              {isSubmitting ? t('conferimenti.respingi_modal.salvataggio') : t('conferimenti.respingi_modal.respingi')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
